import type { Json, TurnErrorKind } from '../types';

/**
 * What a status means for the person waiting, rather than for the developer
 * reading the log. A failed request is different from an unavailable service.
 */
export const errorKind = (status: number): TurnErrorKind => {
  if (status === 401 || status === 403) return 'unauthorised';
  if (status === 429) return 'busy';
  if ([0, 502, 503, 504].includes(status)) return 'unreachable';

  return 'failed';
};

export class HttpError extends Error {
  readonly status: number;
  readonly kind: TurnErrorKind;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.kind = errorKind(status);
  }
}

/**
 * JSON over `fetch`, with the one behaviour every caller in this add-on
 * wants: a non-2xx answer is an `HttpError` carrying the status, and the
 * detail the service put in the body rather than a generic message.
 *
 * A subclass is the place to add a header to every request — an auth
 * scheme, a tenant id — without touching a single call site.
 */
export class HttpTransport {
  constructor(protected readonly baseUrl: string) {}

  protected headers(hasBody: boolean): Record<string, string> {
    return hasBody ? { 'Content-Type': 'application/json' } : {};
  }

  async request<T = Json>(
    path: string,
    { method = 'GET', body }: { method?: string; body?: unknown } = {},
  ): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: this.headers(body !== undefined),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    let data: any = null;
    try {
      data = await response.json();
    } catch {
      /* non-JSON error body */
    }

    if (!response.ok) {
      throw new HttpError(
        (data && (data.detail || data.message)) ||
          `The assistant service answered ${response.status}.`,
        response.status,
      );
    }

    return data as T;
  }
}
